import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building, Calendar, Eye, FileMinus, FileText, Plus, RefreshCw, Search, Send, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CreditNoteFormModal } from '../components/documentForms/CreditNoteFormModal';
import { SdiStatusBadge } from '../components/SdiStatusBadge';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';
import { formatCurrency, formatDate } from '../lib/exportUtils';
import { cn } from '../lib/cn';
import { InvoiceDetailPage } from './InvoiceDetailPage';
import { InvoicePrintView } from './InvoicePrintView';

interface CreditNoteRow {
  id: string;
  invoice_number: string | null;
  invoice_date: string;
  status: string;
  total_amount: number | null;
  credit_note_reason: string | null;
  reference_invoice_id: string | null;
  sdi_status: string | null;
  sdi_uuid: string | null;
  customers: { company_name: string } | null;
}

interface ReferenceRow {
  id: string;
  invoice_number: string | null;
  invoice_date: string;
}

const STATUS_STYLES: Record<string, string> = {
  issued: 'bg-green-500/10 text-green-500 border-green-500/20',
  draft: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
};

const STATUS_LABELS: Record<string, string> = { issued: 'Emessa', draft: 'Bozza', cancelled: 'Annullata' };

export default function CreditNotesPage() {
  const { addNotification } = useNotifications();

  const [rows, setRows] = useState<CreditNoteRow[]>([]);
  const [references, setReferences] = useState<Record<string, ReferenceRow>>({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [year, setYear] = useState<string>(() => String(new Date().getFullYear()));
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [printId, setPrintId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('invoices')
      .select('id, invoice_number, invoice_date, status, total_amount, credit_note_reason, reference_invoice_id, sdi_status, sdi_uuid, customers(company_name)')
      .eq('document_type', 'credit_note')
      .order('invoice_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Errore caricamento note di credito:', error);
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare le note di credito' });
      setLoading(false);
      return;
    }

    const list = (data || []) as unknown as CreditNoteRow[];
    setRows(list);

    // Numero e data delle fatture rettificate, in una sola lettura
    const refIds = [...new Set(list.map((r) => r.reference_invoice_id).filter(Boolean))] as string[];
    if (refIds.length > 0) {
      const { data: refs, error: refError } = await supabase
        .from('invoices')
        .select('id, invoice_number, invoice_date')
        .in('id', refIds);
      if (refError) console.warn('Errore caricamento fatture rettificate:', refError);
      setReferences(Object.fromEntries(((refs || []) as ReferenceRow[]).map((r) => [r.id, r])));
    } else {
      setReferences({});
    }
    setLoading(false);
  }, [addNotification]);

  useEffect(() => {
    load();
  }, [load]);

  const years = useMemo(() => {
    const set = new Set(rows.map((r) => r.invoice_date.slice(0, 4)));
    set.add(String(new Date().getFullYear()));
    return [...set].sort().reverse();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (year !== 'all' && !r.invoice_date.startsWith(year)) return false;
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      if (!q) return true;
      const ref = r.reference_invoice_id ? references[r.reference_invoice_id] : null;
      return [r.invoice_number, r.customers?.company_name, r.credit_note_reason, ref?.invoice_number]
        .some((v) => v?.toLowerCase().includes(q));
    });
  }, [rows, references, search, filterStatus, year]);

  const stats = useMemo(() => {
    const issued = filtered.filter((r) => r.status === 'issued');
    return {
      total: issued.reduce((sum, r) => sum + (r.total_amount || 0), 0),
      issued: issued.length,
      toSend: issued.filter((r) => !r.sdi_uuid && !r.sdi_status).length,
      drafts: filtered.filter((r) => r.status === 'draft').length,
    };
  }, [filtered]);

  const kpis = [
    { label: 'Totale stornato', value: `−${formatCurrency(stats.total)}`, icon: TrendingDown, color: 'text-rose-500' },
    { label: 'Emesse', value: stats.issued, icon: FileMinus, color: 'text-green-500' },
    { label: 'Da inviare SDI', value: stats.toSend, icon: Send, color: 'text-blue-500' },
    { label: 'Bozze', value: stats.drafts, icon: FileText, color: 'text-yellow-500' },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-blue-900">Note di credito</h1>
          <p className="text-sm text-neutral-400 mt-1">Storni totali o parziali delle fatture emesse</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => load()} className="h-10" title="Aggiorna">
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
          <Button
            onClick={() => setShowForm(true)}
            className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white flex-1 md:flex-none"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuova nota di credito
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="border-gray-200 bg-white">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] md:text-sm font-medium text-neutral-500 uppercase tracking-wider">{kpi.label}</p>
                <kpi.icon className={cn('h-3 w-3 md:h-4 md:w-4', kpi.color)} />
              </div>
              <div className="text-sm md:text-2xl font-bold text-gray-900 truncate">{kpi.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Ricerca e filtri */}
      <Card className="p-1">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <Input
              placeholder="Cerca per numero, cliente, motivo o fattura..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 md:h-12 border-none bg-transparent focus-visible:ring-0 text-base"
            />
          </div>
          <div className="flex gap-2 px-2 pb-2 sm:p-0 sm:pr-2">
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-9 w-28 bg-white border-gray-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli anni</SelectItem>
                {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9 w-32 bg-white border-gray-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="issued">Emesse</SelectItem>
                <SelectItem value="draft">Bozze</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Lista */}
      <div className="grid grid-cols-1 gap-4">
        {loading && rows.length === 0 ? (
          [...Array(3)].map((_, i) => <Card key={i} className="h-24 animate-pulse bg-gray-50 border-gray-100" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 rounded-xl">
            <FileMinus className="w-12 h-12 text-neutral-600 mx-auto mb-4 opacity-20" />
            <p className="text-neutral-400">
              {rows.length === 0 ? 'Nessuna nota di credito emessa finora' : 'Nessuna nota di credito con questi filtri'}
            </p>
          </div>
        ) : (
          filtered.map((nc) => {
            const ref = nc.reference_invoice_id ? references[nc.reference_invoice_id] : null;
            return (
              <Card
                key={nc.id}
                className="group p-4 hover:shadow-xl transition-all duration-300 cursor-pointer border-gray-200 hover:border-rose-400 bg-white relative overflow-hidden"
                onClick={() => setDetailId(nc.id)}
              >
                <div className="absolute top-0 left-0 w-1 h-0 group-hover:h-full bg-rose-500 transition-all duration-300" />

                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                  <div className="flex-1 min-w-0 basis-1/2 sm:basis-auto">
                    <h3 className="font-bold text-lg text-gray-900 group-hover:text-rose-700 transition-colors truncate">
                      {nc.invoice_number || <span className="text-neutral-500 italic">Bozza</span>}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <div className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border', STATUS_STYLES[nc.status])}>
                        {STATUS_LABELS[nc.status] ?? nc.status}
                      </div>
                      <span className="text-[11px] text-neutral-500">
                        {ref
                          ? `Rettifica fattura ${ref.invoice_number || 'bozza'} del ${formatDate(ref.invoice_date)}`
                          : 'Nessuna fattura collegata'}
                      </span>
                    </div>
                  </div>

                  {nc.status === 'issued' && (
                    <div className="flex-shrink-0 order-last w-full sm:order-none sm:w-auto">
                      <SdiStatusBadge sdiStatus={nc.sdi_status} sdiUuid={nc.sdi_uuid} />
                    </div>
                  )}

                  <div className="text-right flex flex-col items-end gap-2 flex-shrink-0 sm:flex-1 min-w-0">
                    <div className="text-lg font-black text-rose-700">−{formatCurrency(nc.total_amount || 0)}</div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-teal-600 hover:bg-teal-50 rounded-full"
                      title="Anteprima A4"
                      onClick={(e) => { e.stopPropagation(); setPrintId(nc.id); }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3 text-sm text-neutral-500">
                  <div className="flex items-center min-w-0">
                    <Building className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="truncate">{nc.customers?.company_name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span>{formatDate(nc.invoice_date)}</span>
                  </div>
                  {nc.credit_note_reason && (
                    <div className="truncate md:col-span-1" title={nc.credit_note_reason}>
                      Motivo: {nc.credit_note_reason}
                    </div>
                  )}
                </div>
              </Card>
            );
          })
        )}
      </div>

      <CreditNoteFormModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onCreated={(nc) => {
          setShowForm(false);
          load();
          setDetailId(nc.id);
        }}
      />

      <InvoiceDetailPage
        isOpen={detailId !== null}
        onClose={() => setDetailId(null)}
        invoiceId={detailId || undefined}
        mode="view"
        onSaveSuccess={load}
        onOpenDocument={setDetailId}
      />

      {printId && (
        <InvoicePrintView isOpen={Boolean(printId)} onClose={() => setPrintId(null)} invoiceId={printId} />
      )}
    </div>
  );
}
